import { useMemo, useRef, useState } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import type { A2uiComponent, A2uiContext, A2uiSurface } from '@/types/a2ui';
import { resolvePath, resolveValue, setPath } from './a2uiUtils';
import './A2UIRenderer.css';

const ICONS: Record<string, string> = {
  accountCircle: '👤',
  person: '👤',
  payment: '💳',
  call: '📞',
  phone: '📞',
  settings: '⚙️',
  check: '✅',
  mail: '✉️',
  info: 'ℹ️',
  warning: '⚠️',
  error: '❌',
  refresh: '🔄',
  help: '❓',
};

const JUSTIFY: Record<string, string> = {
  start: 'flex-start',
  end: 'flex-end',
  center: 'center',
  spaceBetween: 'space-between',
  spaceAround: 'space-around',
  spaceEvenly: 'space-evenly',
  stretch: 'stretch',
};

const ALIGN: Record<string, string> = {
  start: 'flex-start',
  end: 'flex-end',
  center: 'center',
  stretch: 'stretch',
};

interface NodeProps {
  id: string;
  byId: Record<string, A2uiComponent>;
  ctx: A2uiContext;
  onAction: (message: string) => void;
}

function renderMarkdown(text: string): { __html: string } {
  return { __html: DOMPurify.sanitize(marked.parse(text, { async: false }) as string) };
}

function A2UINode({ id, byId, ctx, onAction }: NodeProps) {
  const node = byId[id];
  const [disabled, setDisabled] = useState(false);
  if (!node) return null;

  switch (node.component) {
    case 'Card':
      return (
        <div className="a2ui-card">
          <A2UINode id={node.child} byId={byId} ctx={ctx} onAction={onAction} />
        </div>
      );

    case 'Divider':
      return <hr className="a2ui-divider" />;

    case 'Icon':
      return <span className="a2ui-icon">{ICONS[node.name] ?? ''}</span>;

    case 'Row':
    case 'Column':
    case 'List': {
      const baseClass = node.component === 'Row' ? 'a2ui-row' : node.component === 'Column' ? 'a2ui-col' : 'a2ui-list';
      const flexDirection =
        node.component === 'List'
          ? node.direction === 'horizontal'
            ? 'row'
            : 'column'
          : node.component === 'Row'
            ? 'row'
            : 'column';
      const style = {
        flexDirection,
        justifyContent: node.justify ? (JUSTIFY[node.justify] ?? 'flex-start') : undefined,
        alignItems: ALIGN[node.align ?? ''] ?? 'stretch',
      } as React.CSSProperties;

      if (Array.isArray(node.children)) {
        return (
          <div className={baseClass} style={style}>
            {node.children.map((childId) => (
              <A2UINode key={childId} id={childId} byId={byId} ctx={ctx} onAction={onAction} />
            ))}
          </div>
        );
      }

      const list = resolvePath(ctx, node.children.path);
      const items = Array.isArray(list) ? list : [];
      return (
        <div className={baseClass} style={style}>
          {items.map((item, index) => (
            <A2UINode
              key={index}
              id={(node.children as { componentId: string }).componentId}
              byId={byId}
              ctx={{ root: ctx.root, scope: item as Record<string, unknown> }}
              onAction={onAction}
            />
          ))}
        </div>
      );
    }

    case 'Text': {
      const text = resolveValue(node.text, ctx);
      return (
        <div
          className={`a2ui-text${node.variant === 'caption' ? ' a2ui-caption' : ''}`}
          dangerouslySetInnerHTML={renderMarkdown(text)}
        />
      );
    }

    case 'Button': {
      const variantClass =
        node.variant === 'primary' ? ' a2ui-btn-primary' : node.variant === 'borderless' ? ' a2ui-btn-borderless' : '';
      const handleClick = () => {
        const event = node.action?.event;
        if (!event) return;
        const message = event.userMessage != null ? resolveValue(event.userMessage, ctx) : event.name;
        setDisabled(true);
        onAction(message);
        setDisabled(false);
      };
      return (
        <button type="button" className={`a2ui-btn${variantClass}`} disabled={disabled} onClick={handleClick}>
          <A2UINode id={node.child} byId={byId} ctx={ctx} onAction={onAction} />
        </button>
      );
    }

    case 'TextField': {
      const labelText = node.label != null ? resolveValue(node.label, ctx) : '';
      const placeholder = node.placeholder != null ? resolveValue(node.placeholder, ctx) : undefined;
      const defaultValue = resolveValue(node.value, ctx);
      const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (node.value && typeof node.value === 'object' && 'path' in node.value) {
          setPath(ctx, node.value.path, e.target.value);
        }
      };
      return (
        <div className="a2ui-field">
          {labelText && <label className="a2ui-field-label">{labelText}</label>}
          {node.variant === 'longText' ? (
            <textarea
              className="a2ui-input"
              defaultValue={defaultValue}
              placeholder={placeholder}
              onChange={handleChange}
            />
          ) : (
            <input
              className="a2ui-input"
              type={node.variant === 'obscured' ? 'password' : 'text'}
              defaultValue={defaultValue}
              placeholder={placeholder}
              onChange={handleChange}
            />
          )}
        </div>
      );
    }

    default:
      return null;
  }
}

export interface A2UIRendererProps {
  ui: A2uiSurface;
  /** Called when a user interacts with an in-card action (e.g. a Button), as if they'd typed the resulting message. */
  onAction: (message: string) => void;
}

/** Renders an A2UI (v1.0, Basic Catalog subset) component tree returned by the agent. */
export default function A2UIRenderer({ ui, onAction }: A2UIRendererProps) {
  const rootRef = useRef<Record<string, unknown>>(ui.dataModel ?? {});
  const byId = useMemo(() => {
    const map: Record<string, A2uiComponent> = {};
    for (const component of ui.components ?? []) {
      map[component.id] = component;
    }
    return map;
  }, [ui.components]);

  const ctx: A2uiContext = { root: rootRef.current, scope: rootRef.current };

  return (
    <div className="a2ui-surface">
      <A2UINode id="root" byId={byId} ctx={ctx} onAction={onAction} />
    </div>
  );
}
